import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

export default function Consent() {
  const { user, acceptTerms, logout, refreshUser, isAuthenticated } = useAuth();
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // If already accepted terms, redirect to dashboard immediately
  useEffect(() => {
    if (user && user.accepted_terms) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  // Set initial name from user object if it exists (e.g. from Google login)
  useEffect(() => {
    if (user && user.name) {
      setName(user.name);
    }
  }, [user]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!accepted) {
      showError("You must accept the terms of service to continue.");
      return;
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      showError("Name is required.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await acceptTerms(trimmedName);
      if (res.success) {
        showSuccess("Profile completed successfully!");
        navigate("/dashboard", { replace: true });
      } else {
        showError(res.error || "Failed to update profile.");
      }
    } catch (err) {
      showError("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [name, accepted, acceptTerms, navigate, showError, showSuccess]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate("/login?mode=signin", { replace: true });
    } catch (err) {
      showError("Failed to sign out.");
    }
  }, [logout, navigate, showError]);

  if (!user) {
    return (
      <div className="auth-shell">
        <div className="auth-container">
          <div className="auth-card card" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <div className="loading">Loading user profile…</div>
          </div>
        </div>
      </div>
    );
  }

  // We request name input if they don't have an avatar (typically email OTP signin)
  // or if their name is not set.
  const hasAvatar = !!user.avatar_url;
  const needsNameInput = true;

  // Generate fallback avatar initial letter
  const initialLetter = (user.name || user.email || "?").charAt(0).toUpperCase();

  return (
    <div className="auth-shell">
      <div className="auth-container">
        <div className="auth-card card" style={{ maxWidth: '440px', width: '100%', margin: '40px auto', textAlign: 'center' }}>
          <div className="auth-brand" style={{ justifyContent: 'center', marginBottom: '24px' }}>
            <img src="/logo.svg" alt="Nova SEO Logo" style={{ height: "40px", width: "40px", borderRadius: "10px", objectFit: "contain" }} />
            <div style={{ textAlign: 'left' }}>
              <div className="auth-brand-title" style={{ fontSize: '1.2rem', fontWeight: 700 }}>Nova SEO</div>
              <div className="auth-brand-subtitle" style={{ fontSize: '0.75rem' }}>Legal Consent & Profile Setup</div>
            </div>
          </div>

          <h1 className="auth-title" style={{ fontSize: '1.5rem', marginBottom: '8px', fontWeight: 700 }}>Let's get started</h1>
          <p className="auth-subtitle" style={{ fontSize: '0.85rem', marginBottom: '24px', color: 'var(--text-muted)' }}>
            Please read and accept the terms to complete your profile
          </p>

          <form className="auth-form" onSubmit={handleSubmit}>
            {/* User Profile Card */}
            <div style={{
              background: 'rgba(37, 99, 235, 0.03)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '20px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '14px',
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
            }}>
              {/* Profile Avatar */}
              {hasAvatar ? (
                <img
                  src={user.avatar_url}
                  alt="Profile"
                  referrerPolicy="no-referrer"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    border: '2px solid #fff',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    // Fallback if image fails to load
                    e.target.style.display = 'none';
                    document.getElementById('avatar-fallback').style.display = 'flex';
                  }}
                />
              ) : null}

              {/* Avatar Fallback */}
              <div
                id="avatar-fallback"
                style={{
                  display: hasAvatar ? 'none' : 'flex',
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: '#fff',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  boxShadow: '0 2px 6px rgba(37, 99, 235, 0.2)'
                }}
              >
                {initialLetter}
              </div>

              {/* User Info Container */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', flex: 1, minWidth: 0 }}>
                {/* Prefilled Name (Google users) */}
                {!needsNameInput && (
                  <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-heading)', marginBottom: '2px' }}>
                    {user.name}
                  </div>
                )}

                {/* Email Address */}
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                  {user.email}
                </div>
              </div>
            </div>

            {/* Name Input Field (Email users / missing name) */}
            {needsNameInput && (
              <div style={{ textAlign: 'left', marginBottom: '20px' }}>
                <label className="field-label" style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-heading)' }}>
                  Name
                </label>
                <input
                  type="text"
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  required
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
            )}

            {/* Legal Consent Checkbox */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '24px', textAlign: 'left' }}>
              <input
                type="checkbox"
                id="termsCheckbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                style={{ marginTop: '4px', cursor: 'pointer', transform: 'scale(1.1)' }}
                required
              />
              <label htmlFor="termsCheckbox" style={{ fontSize: '0.85rem', color: 'var(--text-muted)', cursor: 'pointer', lineHeight: '1.4', userSelect: 'none' }}>
                I agree to the <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>Terms of Service</a>, <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>Privacy Policy</a>, and <a href="/model-terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>Model Terms</a>.
              </label>
            </div>

            {/* Continue Button */}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !accepted || (needsNameInput && !name.trim())}
              style={{ width: '100%', padding: '12px', fontSize: '0.95rem', borderRadius: 'var(--radius-sm)', transition: 'all 0.2s' }}
            >
              {submitting ? "Signing in..." : "Continue"}
            </button>
          </form>

          {/* Switch Account / Logout Footnote */}
          <div className="auth-footnote" style={{ marginTop: '24px', fontSize: '0.85rem' }}>
            Already have an account?{" "}
            <button
              type="button"
              className="auth-link-btn"
              onClick={handleLogout}
              style={{
                border: 'none',
                background: 'none',
                padding: 0,
                color: 'var(--accent)',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: 'inherit',
                textDecoration: 'underline'
              }}
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
